import type { CameraConfiguration } from '@sb/integrations/implementations/Camera/types';
import { FrameType } from '@sb/messenger/auto_generated/messenger/camera_bot_service';

/**
 * Resolves which camera-bot stream a RunSkill device should read from, given
 * an equipment's camera configuration and (optionally) the bot-hardware slot
 * it's bound to.
 *
 * Precedence: first COLOR stream; if none, and the bound hardware isn't a
 * depth sensor, the first COLOR_FRONT stream on dual-lens devices (e.g.
 * Insta360) — the front-lens default; else the device-id fallback. A
 * depth-sensor hardware slot always wants the DEPTH stream, never a lens's
 * color feed.
 */
export function resolveCameraStreamId(
  cameraConfig: CameraConfiguration,
  botHardware?: { equipmentType: string },
): { streamId: string; frameType?: string } {
  // Check if this camera is a depth sensor based on hardware config
  let streamId: string = cameraConfig.id.replaceAll(' ', '_');
  let frameType: string | undefined;

  const colorStream = cameraConfig.availableStreams.find(
    (stream) => stream.frameType === FrameType.COLOR,
  );

  if (colorStream) {
    streamId = colorStream.key;
  } else if (botHardware?.equipmentType !== 'depth_sensor') {
    const frontStream = cameraConfig.availableStreams.find(
      (stream) => stream.frameType === FrameType.COLOR_FRONT,
    );

    if (frontStream) {
      streamId = frontStream.key;
    }
  }

  if (botHardware && botHardware.equipmentType === 'depth_sensor') {
    const depthStream = cameraConfig.availableStreams.find(
      (stream) => stream.frameType === FrameType.DEPTH,
    );

    if (depthStream) {
      streamId = depthStream.key;
    } else {
      streamId = `${streamId}_depth`;
    }

    frameType = 'uint16'; // TODO use the FrameType enum
  }

  return { streamId, frameType };
}
